import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

interface EmailTemplate {
  id: string;
  chave: string;
  assunto: string;
  corpo: string;
  descricao: string | null;
  variaveis: string[] | null;
  ativo: boolean;
  criado_em: string;
}

const VARIAVEIS_SUGERIDAS = [
  'nome', 'email', 'carreira', 'nivel_atual', 'pontos',
  'formacao_nome', 'data_inicio', 'data_fim', 'local',
  'camara_nome', 'mes', 'total_formacoes', 'total_participantes', 'total_horas'
];

export default function EmailTemplatesTab() {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  const [editTemplate, setEditTemplate] = useState<EmailTemplate | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [previewVars, setPreviewVars] = useState<Record<string, string>>();

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    const { data, error: err } = await supabase
      .from('email_templates')
      .select('*')
      .order('chave');
    if (err) {
      setError('Erro ao carregar templates: ' + err.message);
    } else {
      setTemplates(data ?? []);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const handleSave = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!editTemplate) return;
    setError('');
    setMsg('');

    const formData = new FormData(e.currentTarget);
    const chave = String(formData.get('chave') ?? '').trim();
    const assunto = String(formData.get('assunto') ?? '').trim();
    const corpo = String(formData.get('corpo') ?? '').trim();
    const descricao = String(formData.get('descricao') ?? '').trim() || null;
    const variaveisStr = String(formData.get('variaveis') ?? '').trim();
    const variaveis = variaveisStr ? variaveisStr.split(',').map(v => v.trim()).filter(Boolean) : [];

    if (!chave || !assunto || !corpo) {
      setError('Chave, assunto e corpo são obrigatórios.');
      return;
    }

    const data = {
      chave,
      assunto,
      corpo,
      descricao,
      variaveis,
      atualizado_em: new Date().toISOString(),
    };

    const { error: err } = editTemplate.id
      ? await supabase.from('email_templates').update(data).eq('id', editTemplate.id)
      : await supabase.from('email_templates').insert({ ...data, ativo: true });

    if (err) {
      setError('Erro ao guardar: ' + err.message);
    } else {
      setMsg(editTemplate.id ? 'Template atualizado!' : 'Template criado!');
      setEditTemplate(null);
      setShowAdd(false);
      await fetchTemplates();
      setTimeout(() => setMsg(''), 4000);
    }
  };

  const handleToggle = async (t: EmailTemplate) => {
    setError('');
    const { error: err } = await supabase
      .from('email_templates')
      .update({ ativo: !t.ativo, atualizado_em: new Date().toISOString() })
      .eq('id', t.id);
    if (err) {
      setError('Erro: ' + err.message);
    } else {
      setMsg(`Template "${t.chave}" ${!t.ativo ? 'ativado' : 'desativado'}.`);
      await fetchTemplates();
      setTimeout(() => setMsg(''), 4000);
    }
  };

  const handleDelete = async (t: EmailTemplate) => {
    if (!confirm(`Eliminar template "${t.chave}"?`)) return;
    setError('');
    const { error: err } = await supabase.from('email_templates').delete().eq('id', t.id);
    if (err) {
      setError('Erro ao eliminar: ' + err.message);
    } else {
      setMsg('Template eliminado.');
      await fetchTemplates();
      setTimeout(() => setMsg(''), 4000);
    }
  };

  const renderPreview = (corpo: string, vars: string[] | null): string => {
    let result = corpo;
    const varsToUse = vars ?? [];
    varsToUse.forEach((v) => {
      const val = previewVars[v] ?? `{{${v}}}`;
      result = result.replace(new RegExp(`{{${v}}}`, 'g'), val);
    });
    return result;
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-2xl border border-zinc-100 p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-zinc-900 rounded-xl flex-shrink-0">
              <i className="ri-mail-settings-line text-white text-lg" />
            </div>
            <div>
              <h2 className="text-[15px] font-semibold text-zinc-800">Templates de Notificações</h2>
              <p className="text-[11px] text-zinc-400 mt-0.5">Personalizar assunto e corpo dos emails automáticos do sistema</p>
            </div>
          </div>
          <button
            onClick={() => { setEditTemplate({ id: '', chave: '', assunto: '', corpo: '', descricao: '', variaveis: [], ativo: true, criado_em: '' }); setShowAdd(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-zinc-900 text-white text-[13px] font-medium rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line text-sm" /></div>
            Novo Template
          </button>
        </div>
      </div>

      {/* Feedback */}
      {msg && (
        <div className="flex items-center gap-2.5 p-3.5 bg-emerald-50 border border-emerald-200 rounded-xl">
          <div className="w-4 h-4 flex items-center justify-center flex-shrink-0"><i className="ri-checkbox-circle-fill text-emerald-500 text-sm" /></div>
          <p className="text-[13px] text-emerald-700">{msg}</p>
        </div>
      )}
      {error && (
        <div className="flex items-center gap-2.5 p-3.5 bg-red-50 border border-red-200 rounded-xl">
          <div className="w-4 h-4 flex items-center justify-center flex-shrink-0"><i className="ri-error-warning-line text-red-500 text-sm" /></div>
          <p className="text-[13px] text-red-700">{error}</p>
        </div>
      )}

      {/* Variáveis disponíveis */}
      <div className="bg-white rounded-2xl border border-zinc-100 p-4">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-4 h-4 flex items-center justify-center text-zinc-400"><i className="ri-code-line text-sm" /></div>
          <span className="text-[12px] font-semibold text-zinc-500 uppercase tracking-wider">Variáveis disponíveis</span>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {VARIAVEIS_SUGERIDAS.map((v) => (
            <span key={v} className="text-[11px] font-mono px-2 py-1 bg-zinc-100 text-zinc-600 rounded-md">{'{{' + v + '}}'}</span>
          ))}
        </div>
      </div>

      {/* Lista */}
      <div className="bg-white rounded-2xl border border-zinc-100 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-14">
            <i className="ri-loader-4-line text-2xl text-zinc-300 animate-spin" />
          </div>
        ) : templates.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-14">
            <div className="w-10 h-10 flex items-center justify-center bg-zinc-100 rounded-full">
              <i className="ri-mail-line text-zinc-400 text-lg" />
            </div>
            <p className="text-[13px] text-zinc-400">Nenhum template configurado</p>
          </div>
        ) : (
          <div className="divide-y divide-zinc-50">
            {templates.map((t) => (
              <div key={t.id} className="px-6 py-4 hover:bg-zinc-50/60 transition-colors">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 min-w-0 flex-1">
                    <div className={`w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0 mt-0.5 ${t.ativo ? 'bg-emerald-100' : 'bg-zinc-100'}`}>
                      <i className={`${t.ativo ? 'ri-mail-send-line text-emerald-600' : 'ri-mail-lock-line text-zinc-400'} text-lg`} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-[13px] font-semibold text-zinc-800">{t.chave}</p>
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${t.ativo ? 'bg-emerald-50 text-emerald-600 border border-emerald-200' : 'bg-zinc-100 text-zinc-400 border border-zinc-200'}`}>
                          {t.ativo ? 'Ativo' : 'Inativo'}
                        </span>
                      </div>
                      {t.descricao && <p className="text-[11px] text-zinc-400 mt-0.5">{t.descricao}</p>}
                      <div className="mt-2 p-2.5 bg-zinc-50 rounded-lg border border-zinc-100">
                        <p className="text-[11px] font-semibold text-zinc-500 mb-1">Assunto:</p>
                        <p className="text-[12px] text-zinc-700 font-medium">{t.assunto}</p>
                      </div>
                      <div className="mt-2 p-2.5 bg-zinc-50 rounded-lg border border-zinc-100">
                        <p className="text-[11px] font-semibold text-zinc-500 mb-1">Corpo:</p>
                        <pre className="text-[11px] text-zinc-600 whitespace-pre-wrap font-sans leading-relaxed">{t.corpo}</pre>
                      </div>
                      {t.variaveis && t.variaveis.length > 0 && (
                        <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                          <span className="text-[11px] text-zinc-400">Variáveis:</span>
                          {t.variaveis.map((v) => (
                            <span key={v} className="text-[10px] font-mono px-1.5 py-0.5 bg-zinc-100 text-zinc-500 rounded">{'{{' + v + '}}'}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => handleToggle(t)}
                      className="w-8 h-8 flex items-center justify-center text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-lg transition-colors cursor-pointer"
                      title={t.ativo ? 'Desativar' : 'Ativar'}
                    >
                      <i className={`${t.ativo ? 'ri-pause-line' : 'ri-play-line'} text-sm`} />
                    </button>
                    <button
                      onClick={() => { setEditTemplate(t); setShowAdd(true); }}
                      className="w-8 h-8 flex items-center justify-center text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-lg transition-colors cursor-pointer"
                      title="Editar"
                    >
                      <i className="ri-edit-line text-sm" />
                    </button>
                    <button
                      onClick={() => handleDelete(t)}
                      className="w-8 h-8 flex items-center justify-center text-zinc-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                      title="Eliminar"
                    >
                      <i className="ri-delete-bin-line text-sm" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal Add/Edit */}
      {showAdd && editTemplate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl border border-zinc-200 shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-100">
              <h3 className="text-[15px] font-semibold text-zinc-800">{editTemplate.id ? 'Editar Template' : 'Novo Template'}</h3>
              <button onClick={() => { setShowAdd(false); setEditTemplate(null); }} className="w-8 h-8 flex items-center justify-center text-zinc-400 hover:text-zinc-700 rounded-lg transition-colors cursor-pointer">
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[12px] font-semibold text-zinc-500 uppercase tracking-wider mb-1.5">Chave (identificador único)</label>
                  <input name="chave" defaultValue={editTemplate.chave} required className="w-full px-3 py-2 text-sm bg-zinc-50 border border-zinc-200 rounded-lg text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900/10 focus:border-zinc-400 transition-colors" placeholder="ex: progressao_alerta" />
                </div>
                <div>
                  <label className="block text-[12px] font-semibold text-zinc-500 uppercase tracking-wider mb-1.5">Assunto do Email</label>
                  <input name="assunto" defaultValue={editTemplate.assunto} required className="w-full px-3 py-2 text-sm bg-zinc-50 border border-zinc-200 rounded-lg text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900/10 focus:border-zinc-400 transition-colors" placeholder="Assunto do email" />
                </div>
              </div>
              <div>
                <label className="block text-[12px] font-semibold text-zinc-500 uppercase tracking-wider mb-1.5">Descrição</label>
                <input name="descricao" defaultValue={editTemplate.descricao ?? ''} className="w-full px-3 py-2 text-sm bg-zinc-50 border border-zinc-200 rounded-lg text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900/10 focus:border-zinc-400 transition-colors" placeholder="Para que serve este template" />
              </div>
              <div>
                <label className="block text-[12px] font-semibold text-zinc-500 uppercase tracking-wider mb-1.5">Corpo do Email</label>
                <textarea name="corpo" defaultValue={editTemplate.corpo} required rows={8} className="w-full px-3 py-2 text-sm bg-zinc-50 border border-zinc-200 rounded-lg text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900/10 focus:border-zinc-400 transition-colors font-mono leading-relaxed resize-y" placeholder="Olá {{nome}},\n\nEste é o corpo do email..." />
                <p className="text-[11px] text-zinc-400 mt-1">Use {'{{variavel}}'} para placeholders. Suporta texto simples e quebras de linha.</p>
              </div>
              <div>
                <label className="block text-[12px] font-semibold text-zinc-500 uppercase tracking-wider mb-1.5">Variáveis (separadas por vírgula)</label>
                <input name="variaveis" defaultValue={(editTemplate.variaveis ?? []).join(', ')} className="w-full px-3 py-2 text-sm bg-zinc-50 border border-zinc-200 rounded-lg text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900/10 focus:border-zinc-400 transition-colors" placeholder="nome, email, carreira, pontos" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => { setShowAdd(false); setEditTemplate(null); }} className="px-4 py-2 text-[13px] font-medium text-zinc-600 hover:text-zinc-800 transition-colors cursor-pointer">Cancelar</button>
                <button type="submit" className="px-4 py-2 bg-zinc-900 text-white text-[13px] font-medium rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer">
                  {editTemplate.id ? 'Guardar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}